import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { BellIcon } from '@heroicons/react/16/solid'
import { formatDistanceToNow } from 'date-fns'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/Popover'
import { cn } from '@/lib/utils'
import useAuthStore from '@/store/useAuthStore'
import useSwitchTeam from '@/services/api/team/useSwitchTeam'
import {
  type AppNotification,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
  useUnreadNotificationCount,
} from '@/services/api/notifications/useNotifications'

/** In-app notification centre: bell with unread badge and the latest alerts. */
export default function NotificationBell() {
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const currentTeamId = useAuthStore((state) => state.currentTeamId)
  const { data: unread = 0 } = useUnreadNotificationCount()
  const { data, isPending } = useNotifications({ enabled: open })
  const markRead = useMarkNotificationRead()
  const markAllRead = useMarkAllNotificationsRead()
  const { action: switchTeam } = useSwitchTeam()

  const openNotification = async (notification: AppNotification) => {
    setOpen(false)
    if (!notification.read_at) {
      markRead.mutate(notification.id)
    }
    const { team_id: teamId, action_path: path } = notification.data
    // The alert belongs to another Space: switch to it first, so the page it
    // links to shows that Space's data.
    if (teamId && Number(teamId) !== Number(currentTeamId)) {
      await switchTeam({ teamId })
    }
    if (path) {
      navigate(path)
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          iconOnly
          className="relative"
          aria-label={
            unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'
          }
        >
          <BellIcon />
          {unread > 0 && (
            <span className="absolute -right-1 -top-1 flex min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-semibold text-white">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[min(22rem,calc(100vw-2rem))] p-0"
      >
        <div className="flex items-center justify-between border-b px-3 py-2">
          <span className="text-sm font-semibold">Notifications</span>
          {unread > 0 && (
            <button
              type="button"
              className="text-xs text-txt-accent hover:underline"
              onClick={() => markAllRead.mutate(undefined)}
            >
              Mark all as read
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {isPending ? (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">
              Loading…
            </p>
          ) : !data?.data.length ? (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">
              No notifications yet.
            </p>
          ) : (
            data.data.map((notification) => (
              <button
                key={notification.id}
                type="button"
                onClick={() => openNotification(notification)}
                className="flex w-full gap-2 border-b px-3 py-2.5 text-left last:border-b-0 hover:bg-layout-secondary"
              >
                <span
                  className={cn(
                    'mt-1.5 size-2 shrink-0 rounded-full',
                    notification.read_at ? 'bg-transparent' : 'bg-red-600',
                  )}
                />
                <span className="min-w-0 space-y-0.5">
                  {notification.data.title && (
                    <span className="block text-sm font-medium">
                      {notification.data.title}
                    </span>
                  )}
                  <span className="block text-sm text-txt-secondary">
                    {notification.data.message}
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    {notification.data.team_name
                      ? `${notification.data.team_name} · `
                      : ''}
                    {formatDistanceToNow(new Date(notification.created_at), {
                      addSuffix: true,
                    })}
                  </span>
                </span>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
